import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { Ingredient, Recipe, Seconds } from '@/types';

/**
 * Check if a recipe is owned by the given user
 */
export function isOwnRecipe(recipe: Recipe, userId: string | null): boolean {
  if (!userId) return false;
  if (recipe.createdBy) return recipe.createdBy === userId;
  // Legacy fallback: recipes without createdBy
  return recipe.source !== 'By RecipeSense';
}

/**
 * Merge Tailwind CSS classes with clsx
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Generate a unique ID
 */
export function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Format seconds into a human-readable time string
 */
export function formatTime(seconds: Seconds): string {
  if (seconds < 0) return '00:00';
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format seconds into a readable duration string (e.g., "1h 30m")
 */
export function formatDuration(seconds: Seconds): string {
  if (seconds < 60) return `${seconds}s`;
  
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  
  if (hours > 0 && minutes > 0) {
    return `${hours}h ${minutes}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${minutes}m`;
  }
}

/**
 * Parse a duration string (e.g., "1h 30m", "45m", "90s") into seconds
 */
export function parseDuration(input: string): Seconds | null {
  const trimmed = input.trim().toLowerCase();
  
  // Try to parse as "Xh Ym" or "Xh" or "Ym" or "Xs" format
  let totalSeconds = 0;
  let matched = false;
  
  // Hours
  const hourMatch = trimmed.match(/(\d+)\s*h/);
  if (hourMatch?.[1]) {
    totalSeconds += parseInt(hourMatch[1], 10) * 3600;
    matched = true;
  }
  
  // Minutes
  const minMatch = trimmed.match(/(\d+)\s*m(?:in)?/);
  if (minMatch?.[1]) {
    totalSeconds += parseInt(minMatch[1], 10) * 60;
    matched = true;
  }
  
  // Seconds
  const secMatch = trimmed.match(/(\d+)\s*s(?:ec)?/);
  if (secMatch?.[1]) {
    totalSeconds += parseInt(secMatch[1], 10);
    matched = true;
  }
  
  // Try plain number (assume minutes)
  if (!matched) {
    const plainNum = parseInt(trimmed, 10);
    if (!isNaN(plainNum)) {
      totalSeconds = plainNum * 60;
      matched = true;
    }
  }
  
  return matched ? totalSeconds : null;
}

/**
 * Scale ingredient amount by a multiplier
 */
export function scaleIngredient(ingredient: Ingredient, multiplier: number): Ingredient {
  return {
    ...ingredient,
    amount: roundToNiceNumber(ingredient.amount * multiplier),
  };
}

/**
 * Round a number to a "nice" cooking-friendly value
 */
export function roundToNiceNumber(value: number): number {
  if (value === 0) return 0;
  
  // For very small numbers, keep precision
  if (value < 0.1) return Math.round(value * 100) / 100;
  
  // For small numbers (< 1), round to nearest 1/4
  if (value < 1) {
    return Math.round(value * 4) / 4;
  }
  
  // For numbers 1-10, round to nearest 0.5
  if (value < 10) {
    return Math.round(value * 2) / 2;
  }
  
  // For larger numbers, round to nearest whole number
  return Math.round(value);
}

/**
 * Format ingredient for display
 */
export function formatIngredient(ingredient: Ingredient): string {
  const amount = ingredient.amount > 0 ? `${formatAmount(ingredient.amount)} ` : '';
  const unit = ingredient.unit ? `${ingredient.unit} ` : '';
  const optional = ingredient.optional ? ' (optional)' : '';
  const notes = ingredient.notes ? ` - ${ingredient.notes}` : '';
  
  return `${amount}${unit}${ingredient.name}${optional}${notes}`;
}

/**
 * Format an amount nicely (handle fractions)
 */
export function formatAmount(amount: number): string {
  if (amount === 0) return '';
  
  // Check for common fractions
  const fractions: [number, string][] = [
    [0.25, '¼'],
    [0.33, '⅓'],
    [0.5, '½'],
    [0.66, '⅔'],
    [0.75, '¾'],
  ];
  
  const whole = Math.floor(amount);
  const decimal = amount - whole;
  
  // Check if decimal matches a common fraction
  for (const [value, symbol] of fractions) {
    if (Math.abs(decimal - value) < 0.05) {
      if (whole > 0) {
        return `${whole}${symbol}`;
      }
      return symbol;
    }
  }
  
  // Return as decimal or whole number
  if (decimal === 0) {
    return whole.toString();
  }
  
  // Round to 2 decimal places
  return amount.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Get a random item from an array
 */
export function getRandomItem<T>(array: T[]): T | undefined {
  if (array.length === 0) return undefined;
  return array[Math.floor(Math.random() * array.length)];
}

/**
 * Shuffle an array (Fisher-Yates)
 */
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}

/**
 * Debounce a function
 */
export function debounce<T extends (...args: Parameters<T>) => ReturnType<T>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  
  return (...args: Parameters<T>) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(() => {
      func(...args);
    }, wait);
  };
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
}

/**
 * Strip HTML tags from a string
 */
export function stripHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Convert a string to a URL-safe slug
 */
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Get relative time string (e.g., "2 hours ago")
 */
export function getRelativeTime(dateString: string, locale = 'en'): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    if (diffInSeconds < 60) return rtf.format(0, 'second');
    if (diffInSeconds < 3600) return rtf.format(-Math.floor(diffInSeconds / 60), 'minute');
    if (diffInSeconds < 86400) return rtf.format(-Math.floor(diffInSeconds / 3600), 'hour');
    if (diffInSeconds < 604800) return rtf.format(-Math.floor(diffInSeconds / 86400), 'day');
    if (diffInSeconds < 2592000) return rtf.format(-Math.floor(diffInSeconds / 604800), 'week');
  } catch {
    // Fallback for environments without Intl.RelativeTimeFormat
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
  }

  return date.toLocaleDateString(locale);
}

/**
 * Format a date nicely
 */
export function formatDate(dateString: string, locale = 'en'): string {
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Check if the device prefers reduced motion
 */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Check if the device is mobile/tablet (below desktop sidebar breakpoint).
 * Matches the canonical `--bp-lg` (1024px) defined in index.css.
 */
export function isMobile(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(max-width: 1023px)').matches;
}

/**
 * Try to vibrate the device
 */
export function vibrate(pattern: number | number[] = 200): void {
  if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
    navigator.vibrate(pattern);
  }
}

/**
 * Download a file
 */
export function downloadFile(data: string, filename: string, type = 'application/json'): void {
  const blob = new Blob([data], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Read a file as text
 */
export function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
}

/**
 * Focus trap management
 */
export function createFocusTrap(container: HTMLElement): () => void {
  const focusableElements = container.querySelectorAll<HTMLElement>(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  
  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return;
    
    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        lastElement?.focus();
        e.preventDefault();
      }
    } else {
      if (document.activeElement === lastElement) {
        firstElement?.focus();
        e.preventDefault();
      }
    }
  };
  
  container.addEventListener('keydown', handleKeyDown);
  firstElement?.focus();
  
  return () => {
    container.removeEventListener('keydown', handleKeyDown);
  };
}
